import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import type { DocumentVersion } from "@/api/models"
import type { CanvasVersion } from "@/components/canvas-editor/version-history-dropdown"
import type { ContentSidebarPanel } from "@/stores/content-sidebar-store"

import { documentContentBaseUrl } from "@/api/client/documents"
import { ShieldX } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useSidebar } from "@/components/ui/use-sidebar"
import { ARTIFACT_PANEL_ID } from "@/features/artifacts/utils/artifact-panel-id"
import { openSavedArtifact } from "@/features/artifacts/utils/open-saved-artifact"
import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import { closeCanvasInSidebar, openCanvasInSidebar } from "@/features/canvas/utils/canvas-sidebar"
import { useStartNewChat } from "@/features/chat/hooks/use-start-new-chat"
import { DocumentShareDialog } from "@/features/documents/components/document-share-dialog"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useDocument } from "@/hooks/use-documents"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

function mapDocumentVersionsToCanvas(versions: DocumentVersion[] | undefined): CanvasVersion[] {
  return (versions ?? []).map((version) => ({
    version: version.version,
    content: version.content,
    timestamp: version.timestamp,
    is_ai_edit: version.edit_source === "ai",
    ai_edit_preview: version.edit_preview,
  }))
}

export function DocumentEditorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { setOpen, setOpenMobile } = useSidebar()
  const sessionType = useCurrentSessionType()

  const { data: document, isLoading, isFetched } = useDocument(id)
  const canvasDocumentId = useCanvasStore((state) => state.documentId)
  const isCanvasOpen = useContentSidebarStore(
    (state) =>
      state.isOpen && (state.currentPanel?.id === "canvas" || state.pushedPanel?.id === "canvas")
  )
  const isArtifactPanelOpen = useContentSidebarStore(
    (state) =>
      state.isOpen &&
      (state.currentPanel?.id === "artifact" || state.pushedPanel?.id === "artifact")
  )
  const explorerStore = useAIExplorerStore(sessionType)
  const { handleStartNewChat } = useStartNewChat({
    useChatStore: () => explorerStore,
    sessionType,
  })
  const loadedDocumentIdRef = useRef<string | null>(null)
  const preserveCanvasOnUnmountRef = useRef(false)
  const previousIsCanvasOpenRef = useRef(isCanvasOpen)
  const previousIsArtifactPanelOpenRef = useRef(isArtifactPanelOpen)
  const currentDocumentIdRef = useRef(id)
  const pendingArtifactFocusRef = useRef<{
    panel: ContentSidebarPanel
    documentId: string
  } | null>(null)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const backToDocumentsLabel = t("documents.back_to_documents", {
    defaultValue: "Back to documents",
  })
  const chatWithDocumentLabel = t("documents.chat_with_document", {
    defaultValue: "Chat with document",
  })

  useEffect(() => {
    currentDocumentIdRef.current = id
  }, [id])

  // Focuses a deferred saved-artifact open once the user confirms it. Cancel,
  // any other open, leaving this document or unmounting drops it, so a later
  // or unrelated panel never inherits the focus and a manual toggle stays.
  useEffect(() => {
    return useContentSidebarStore.subscribe((state) => {
      const pending = pendingArtifactFocusRef.current
      if (!pending) return
      if (currentDocumentIdRef.current !== pending.documentId) {
        pendingArtifactFocusRef.current = null
      } else if (state.currentPanel === pending.panel) {
        pendingArtifactFocusRef.current = null
        // The canvas-close effect re-armed this ref while confirm was pending.
        // Clear it before that effect sees the confirm-driven true→false drop.
        // A later close of this artifact panel still returns via its own guard.
        previousIsCanvasOpenRef.current = false
        useContentSidebarStore.setState({ isFocused: true })
      } else if (
        state.pendingAction?.type !== "open" ||
        state.pendingAction.panel !== pending.panel
      ) {
        pendingArtifactFocusRef.current = null
      }
    })
  }, [])

  useEffect(() => {
    if (!document) return
    if (loadedDocumentIdRef.current === document.id) return
    pendingArtifactFocusRef.current = null

    // Non-markdown documents are artifacts (html/react/slides/…) and open in the
    // ArtifactPanel, not the markdown canvas. Route them there and mark the doc
    // as loaded so the markdown canvas path below is skipped.
    const artifactType = document.artifactType ?? "markdown"
    if (artifactType !== "markdown") {
      openSavedArtifact(document)
      // A saved artifact opened from Documents fills the workspace, like the
      // markdown canvas below. Chat artifacts keep the split view, and the
      // route-change close (#3580) clears focus when the user leaves. Over a
      // dirty panel the open waits for the "Unsaved changes" dialog, so focus
      // follows once this panel actually becomes current.
      const sidebarState = useContentSidebarStore.getState()
      const deferredPanel =
        sidebarState.pendingAction?.type === "open" ? sidebarState.pendingAction.panel : null
      if (deferredPanel?.id === ARTIFACT_PANEL_ID) {
        pendingArtifactFocusRef.current = { panel: deferredPanel, documentId: document.id }
      } else if (sidebarState.currentPanel?.id === ARTIFACT_PANEL_ID) {
        useContentSidebarStore.setState({ isFocused: true })
      }
      loadedDocumentIdRef.current = document.id
      // A same-turn canvas displacement drops isCanvasOpen true→false. Clear the
      // guard's previous value before it can treat that as "user closed the canvas"
      // and navigate to /documents. A deferred open leaves the canvas current, so
      // the guard re-arms; the subscriber above disarms it again once this artifact
      // panel becomes current.
      previousIsCanvasOpenRef.current = false
      return
    }

    const savedFilenames = document.artifactMeta?.sessionFilenames
    const imageFilenames =
      Array.isArray(savedFilenames) && savedFilenames.every((f) => typeof f === "string")
        ? (savedFilenames as string[])
        : []
    openCanvasInSidebar(
      {
        documentId: document.id,
        activeDocumentCanEdit: document.currentUserRole !== "reader",
        title: document.title,
        content: document.content || "",
        versions: mapDocumentVersionsToCanvas(document.versions),
        lastSavedAt: document.updated ?? null,
        // Saved artifact images live in document-scoped storage (copied on save).
        sessionFilenames: imageFilenames,
        imageContentBaseUrl: imageFilenames.length
          ? documentContentBaseUrl(document.id)
          : undefined,
      },
      {
        title: document.title,
        paneProps: {
          backButtonLabel: backToDocumentsLabel,
          canDeleteDocument: document.currentUserRole === "owner",
          canEditDocument: document.currentUserRole !== "reader",
          canShareDocument: document.currentUserRole === "owner",
          startChatButtonLabel: chatWithDocumentLabel,
          onShareDocument: () => setShareDialogOpen(true),
          onBackToDocuments: () => {
            preserveCanvasOnUnmountRef.current = false
            void navigate("/documents")
          },
          onStartNewChatWithDocument: () => {
            preserveCanvasOnUnmountRef.current = true
            setOpen(false)
            setOpenMobile(false)
            useContentSidebarStore.setState({ isFocused: false })
            handleStartNewChat()
            window.setTimeout(() => {
              if (window.location.pathname.startsWith("/documents/")) {
                preserveCanvasOnUnmountRef.current = false
              }
            }, 0)
          },
        },
      }
    )
    useContentSidebarStore.setState({ isFocused: true })
    loadedDocumentIdRef.current = document.id
    // Mirror of the artifact path's previousIsCanvasOpenRef reset above: opening
    // the canvas for a markdown doc displaces any open artifact panel, dropping
    // isArtifactPanelOpen true→false. Without this reset the artifact-close guard
    // would see wasArtifactPanelOpen=true && !isArtifactPanelOpen && id matches
    // and bounce the user to /documents. Neutralise that transition here.
    previousIsArtifactPanelOpenRef.current = false
  }, [
    backToDocumentsLabel,
    chatWithDocumentLabel,
    document,
    handleStartNewChat,
    navigate,
    setOpen,
    setOpenMobile,
  ])

  useEffect(() => {
    if (!id) {
      previousIsCanvasOpenRef.current = isCanvasOpen
      return
    }

    const wasCanvasOpen = previousIsCanvasOpenRef.current
    previousIsCanvasOpenRef.current = isCanvasOpen

    if (wasCanvasOpen && !isCanvasOpen && loadedDocumentIdRef.current === id) {
      void navigate("/documents")
    }
  }, [id, isCanvasOpen, navigate])

  // Non-markdown docs open in the ArtifactPanel, not the canvas. Mirror the
  // canvas-close guard above: when the artifact panel closes for this loaded
  // document, route back to /documents so the user is not stranded on the empty
  // /documents/:id shell (which renders nothing but the share dialog).
  useEffect(() => {
    if (!id) {
      previousIsArtifactPanelOpenRef.current = isArtifactPanelOpen
      return
    }

    const wasArtifactPanelOpen = previousIsArtifactPanelOpenRef.current
    previousIsArtifactPanelOpenRef.current = isArtifactPanelOpen

    if (wasArtifactPanelOpen && !isArtifactPanelOpen && loadedDocumentIdRef.current === id) {
      void navigate("/documents")
    }
  }, [id, isArtifactPanelOpen, navigate])

  useEffect(() => {
    return () => {
      if (preserveCanvasOnUnmountRef.current) {
        return
      }
      if (canvasDocumentId && canvasDocumentId === id) {
        closeCanvasInSidebar()
      }
    }
  }, [canvasDocumentId, id])

  if (!id) {
    return null
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="size-8" />
      </div>
    )
  }

  const documentNotFound = isFetched && !document
  if (documentNotFound) {
    return (
      <div className="mx-auto mt-24 max-w-md px-6 text-center">
        <div className="bg-destructive/10 mb-6 inline-flex rounded-full p-8">
          <ShieldX className="text-destructive/60 size-12" />
        </div>
        <h2 className="mb-2 text-xl font-semibold">
          {t("documents.not_found_title", { defaultValue: "Document not found" })}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("documents.not_found_description", {
            defaultValue: "This document doesn't exist or you don't have access to it.",
          })}
        </p>
        <Button onClick={() => navigate("/documents")}>
          {t("documents.back_to_documents", { defaultValue: "Back to documents" })}
        </Button>
      </div>
    )
  }

  return (
    <div className="h-[calc(100svh-64px)] min-h-[calc(100svh-64px)]">
      {document && (
        <DocumentShareDialog
          currentUserRole={document.currentUserRole}
          documentId={document.id}
          open={shareDialogOpen}
          ownerUserId={document.ownerUserId}
          onOpenChange={setShareDialogOpen}
        />
      )}
    </div>
  )
}
